import { Operator } from "@/models"
import { Instant, PlainDate } from "@/temporal"
import { toRaw } from "vue"
import { EnumSchema, type ApiSchema, type Component } from "vue-utils"
import { getDisplayComponent, type GenericDisplayProps } from "../displays"
import BooleanInput from "./BooleanInput"
import DateInput from "./DateInput"
import { createEnumInput } from "./EnumInput"
import { createFilterArrayInput } from "./FilterArrayInput"
import InstantInput from "./InstantInput"
import NumberInput from "./NumberInput"
import StringInput from "./StringInput"

export interface GenericInputProps<T> {
	value: T | null
	setValue(value: T): void
}

const inputComponents = new Map<ApiSchema<unknown>, Component<GenericInputProps<unknown>>>()

function addInputComponent<T>(schema: ApiSchema<T>, component: Component<GenericInputProps<T>>) {
	inputComponents.set(schema as ApiSchema<unknown>, component as Component<GenericInputProps<unknown>>)
}

function getRawInputComponent<T>(schema: ApiSchema<T>): Component<GenericInputProps<T>> | null {
	const component = inputComponents.get(schema as ApiSchema<unknown>) as Component<GenericInputProps<T>>
	if (component) {
		return component
	}
	if (schema instanceof EnumSchema) {
		return createEnumInput(schema) as Component<GenericInputProps<T>>
	}
	return null
}

export function getInputComponent<T>(schema: ApiSchema<T>, operator: Operator): Component<GenericInputProps<unknown>> {
	if (typeof schema === "object") {
		schema = toRaw(schema)
	}
	const component = getRawInputComponent(schema)
	if (!component) {
		throw new Error("Unknown schema component")
	}
	if (operator === Operator.In || operator === Operator.NotIn) {
		const displayComponent = getDisplayComponent(schema) as Component<GenericDisplayProps<T>>
		return createFilterArrayInput<T>(component, displayComponent) as Component<GenericInputProps<unknown>>
	}
	return component as Component<GenericInputProps<unknown>>
}

addInputComponent(Number, NumberInput)
addInputComponent(String, StringInput)
addInputComponent(Boolean, BooleanInput)
addInputComponent(PlainDate.Schema, DateInput)
addInputComponent(Instant.Schema, InstantInput)
